import './BescheidModal.module.css';

import {
  SelectGroup,
  Card,
  FileInput,
  FlexSpacer,
  Modal,
  Stack,
  Input,
  Spinner,
  FormRow,
  Button,
  TextArea
} from '@efast_public/fjd-component-library';
import { useState } from 'react';
import UploadsList from '../../pages/shared/UploadsList';
import { useProfile } from '../../shared/hooks/AuthContext';
import {
  FileUploadAction,
  ActionType
} from '../../api/models/UploadFiles/FileUploadAction';
import { UploadFiles } from '../../api/models/UploadFiles/UploadFiles';
import { ConfirmAction } from '../../api/models/UploadFiles/ConfirmAction';
import UploadFilesConfirmationAlert from './UploadFilesConfirmationAlert';
import { ErrorComponentBehoerdenServer } from '../../pages/shared/Error/ErrorComponent';
import { useQuery } from '@tanstack/react-query';
import { CaseDetailsModel } from '../../api/models/Case/CaseDetailsModel';
import { BehApiRoutes } from '../../api/beh-api';

interface SendApplicationModalProps {
  readonly closeOnSecondaryButtonClick: () => void;
  readonly returnOnSecondaryButtonClick: (
    subject: string,
    text: string
  ) => void;
  readonly performApplicationModalActions: (
    subject: string,
    text: string
  ) => void;
  readonly isApplicationPositiveDecided: boolean;
  readonly setApplicationIsPositiveDecided: (
    isPositiveDecided: boolean | undefined
  ) => void;
  readonly subject?: string;
  readonly text?: string;
  readonly isLoadingSend: boolean;
  readonly fileDispatch: (fileAction: FileUploadAction) => void;
  readonly filesUploadstate: UploadFiles;
  readonly confirmAction: ConfirmAction;
  readonly isOpen: boolean;
  readonly caseId: string;
}

export default function SendApplicationModal({
  caseId,
  ...props
}: SendApplicationModalProps) {
  const { data: caseDetails } = useQuery<CaseDetailsModel, Error>({
    queryKey: [BehApiRoutes.case(caseId ?? '')]
  });
  const { profile, error } = useProfile();
  const filesUploadstate: UploadFiles = props.filesUploadstate;
  const [subject, setSubject] = useState(props.subject ?? 'Bescheid:');
  const [text, setText] = useState(props.text ?? '');

  const primaryButtonDisabled = (): boolean => {
    if (props.confirmAction.toConfirm) {
      return false;
    }

    return (
      !subject ||
      !text ||
      props.isApplicationPositiveDecided === undefined ||
      !filesUploadstate.bescheidUpload ||
      props.isLoadingSend
    );
  };

  const primaryButtonlabel = props.confirmAction.toConfirm
    ? props.confirmAction.buttonLabel
    : 'Bescheid senden';

  const heading = props.confirmAction.toConfirm
    ? props.confirmAction.heading
    : 'Bescheid versenden';

  const handlePrimary = () => {
    if (props.confirmAction.toConfirm) {
      props.confirmAction.onConfirmation();
    } else {
      props.performApplicationModalActions(subject, text);
    }
  };

  const handleSecondary = () => {
    if (props.confirmAction.toConfirm) {
      props.confirmAction.onCancel();
    } else {
      props.returnOnSecondaryButtonClick(subject, text);
    }
  };

  return (
    <Modal
      appElement={document.querySelector('.fjd-base-layout') as HTMLElement}
      closeOnBackdropClick={false}
      open={props.isOpen}
      onClose={() => props.closeOnSecondaryButtonClick()}
      data-testid="send-application-modal"
    >
      <Modal.Header id="SendApplicationModal-header">{heading}</Modal.Header>
      <Modal.Content>
        {error ? (
          <ErrorComponentBehoerdenServer />
        ) : props.confirmAction.toConfirm ? (
          <UploadFilesConfirmationAlert confirmAction={props.confirmAction} />
        ) : props.isLoadingSend ? (
          <Spinner />
        ) : (
          <Stack direction="column" spacing="xs">
            <FormRow label="Absender:in der Nachricht">
              <Input
                id="absender"
                value={`${profile?.firstName ?? '?'} ${profile?.lastName ?? '?'}`}
                disabled
              />
            </FormRow>
            <FormRow label="Auftragsnummer & Antragstitel">
              <Input
                id="auftragsnummer"
                value={`${caseDetails?.auftragsnummer} • ${caseDetails?.name}`}
                disabled
              />
            </FormRow>
            <FormRow label="Betreff">
              <Input
                id="betreff"
                data-testid="betreff"
                onChange={(event) => setSubject(event.target.value)}
                value={subject}
                required
              />
            </FormRow>
            <FormRow label="Nachrichtentext">
              <TextArea
                id="nachrichtentext"
                data-testid="nachrichtentext"
                rows={3}
                onChange={(event) => setText(event.target.value)}
                value={text}
                placeholder="Nachrichtentext"
              />
            </FormRow>

            {filesUploadstate.bescheidUpload && (
              <FormRow label="Bescheid">
                <UploadsList
                  applicationIsPositiveDecided={
                    props.isApplicationPositiveDecided
                  }
                  setApplicationIsPositiveDecided={
                    props.setApplicationIsPositiveDecided
                  }
                  uploadFiles={filesUploadstate}
                  dispatch={props.fileDispatch}
                  hasUploadButtonEnabled
                  uploadButtonTextAtLeastOneFileUploaded="Weiteres Dokument hinzufügen"
                  showPreview
                  hasBescheidSelect
                  hasIconsRightAligned
                />
              </FormRow>
            )}
            {!filesUploadstate.bescheidUpload && (
              <Card>
                <div style={{ padding: '8px' }}>
                  <Stack direction="row">
                    <FileInput
                      id="application_upload_button"
                      label="Bescheid hochladen"
                      name="application_upload_button"
                      onChange={(files) => {
                        props.fileDispatch({
                          type: ActionType.ADD_BESCHEID,
                          files: files ?? []
                        });
                      }}
                    />
                    <FlexSpacer />
                    <SelectGroup
                      id="btn-select-group-upload-bescheid-digital"
                      name="result"
                      selectType="single"
                    >
                      <SelectGroup.Button
                        checked={props.isApplicationPositiveDecided ?? false}
                        value="positive"
                        label="✓ Positiv"
                        onClick={() => {
                          props.setApplicationIsPositiveDecided(
                            props.isApplicationPositiveDecided === true
                              ? undefined
                              : true
                          );
                        }}
                      />
                      <SelectGroup.Button
                        checked={
                          props.isApplicationPositiveDecided !== undefined &&
                          !props.isApplicationPositiveDecided
                        }
                        label="✗ Negativ"
                        value="negative"
                        onClick={() => {
                          props.setApplicationIsPositiveDecided(
                            props.isApplicationPositiveDecided === false
                              ? undefined
                              : false
                          );
                        }}
                      />
                    </SelectGroup>
                  </Stack>
                </div>
              </Card>
            )}
          </Stack>
        )}
      </Modal.Content>
      <Modal.Footer>
        {error ? (
          <Button
            variant="primary"
            onClick={() => props.closeOnSecondaryButtonClick()}
          >
            Schließen
          </Button>
        ) : (
          <>
            <Button
              variant="secondary-outline"
              disabled={props.isLoadingSend}
              onClick={handleSecondary}
            >
              {props.confirmAction.toConfirm ? 'Abbrechen' : 'Zurück'}
            </Button>
            <Button
              variant="primary"
              disabled={primaryButtonDisabled()}
              onClick={handlePrimary}
            >
              {primaryButtonlabel}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
